import React, { useEffect } from 'react'
import isFunction from 'lodash/isFunction'
import { MenuItem, Position, Checkbox } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import { Select2, ItemListPredicate } from '@blueprintjs/select'
import { Button, Flex, HighlightedText } from '@ui'
import { getInitials } from '@utils'
import { IUIButtonProps } from '@ui/buttons/Button'
import fuzzy from 'fuzzaldrin-plus'
import { get, startCase } from 'lodash'
import api, { ILocation } from '@api'

interface LocationSelectProps {
  btnText?: React.ReactNode
  value: ILocation | null
  onChange: (location: ILocation) => void
  onSelectAll?: (locations: ILocation[]) => void
  buttonProps?: IUIButtonProps
  itemDisabled?: (location: ILocation) => boolean
  multi?: boolean
  hideButton?: boolean
  itemsQuantity?: number
  selectAllTitle?: string
  excludeItem?: (location: ILocation) => boolean
}

const BPSelect = Select2<ILocation>

function getShortName(name: string) {
  if (name.length === 2) {
    return name.toUpperCase()
  }

  if (name.split(' ').length === 1) {
    return name.slice(0, 2).toUpperCase()
  }

  return getInitials(name)
}

export default function LocationSelect({
  btnText,
  value,
  onChange,
  buttonProps,
  itemDisabled,
  multi,
  hideButton,
  excludeItem,
  onSelectAll,
  itemsQuantity,
  selectAllTitle = 'Select All',
}: LocationSelectProps) {
  const locations: ILocation[] = useLocations()
  const selectAllItemId = `${selectAllTitle}-id`

  // it's for adding item select all if onSelectAll is function.
  const items = !isFunction(onSelectAll)
    ? locations
    : [
        {
          title: selectAllTitle,
          city: '',
          state: '',
          country: '',
          id: selectAllItemId,
        },
        ...locations,
      ]

  return (
    <BPSelect
      resetOnSelect
      popoverProps={{
        minimal: true,
        position: Position.BOTTOM_LEFT,
      }}
      noResults={<MenuItem disabled text="No results" />}
      items={items}
      itemRenderer={(location, { handleClick, modifiers, query }) => {
        if (!modifiers.matchesPredicate) {
          return null
        }

        if (location.id === selectAllItemId && locations.length > 0) {
          // select all item
          return (
            <Item
              key={selectAllItemId}
              query={selectAllTitle}
              onClick={() => {
                onSelectAll?.(locations)
              }}
              checked={itemsQuantity === locations.length}
              disabled={excludeItem?.(location)}
              text={startCase(location.title)}
              multi={multi}
            />
          )
        }

        return (
          <Item
            active={modifiers.active}
            key={location.id}
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              if (multi) {
                onChange(location)
                return
              }
              handleClick(e)
            }}
            checked={itemDisabled?.(location)}
            disabled={excludeItem?.(location)}
            text={`${startCase(location.city)}${
              location.state ? ', ' + getShortName(location.state) : ''
            }`}
            query={query}
            multi={multi}
          />
        )
      }}
      itemListPredicate={filterLocations}
      onItemSelect={onChange}
    >
      {!hideButton && (
        <Button
          {...buttonProps}
          text={value ? value.city : btnText || 'Select'}
          rightIconName={!get(buttonProps, 'iconName') && !value ? 'chevron-down' : undefined}
        />
      )}
    </BPSelect>
  )
}

const filterLocations: ItemListPredicate<ILocation> = (query, locations) => {
  return query ? fuzzy.filter(locations, query, { key: 'city' }) : locations
}

let persistedState: ILocation[] = []

export function useLocations(): ILocation[] {
  const [locations, setLocations] = React.useState<ILocation[]>(persistedState)

  useEffect(() => {
    api.company.locations().then((locations) => setLocations(locations))
  }, [])

  useEffect(() => {
    // Cache initial list
    if (!persistedState.length && locations.length) {
      persistedState = locations
    }
  })

  return locations
}

type ItemProps = {
  text?: string
  query: string
  active?: boolean
  checked?: boolean
  disabled?: boolean
  multi?: boolean
  onClick: (e: React.MouseEvent<HTMLElement>) => void
}

function Item({ text = '', query, active, onClick, disabled, checked, multi }: ItemProps) {
  return (
    <MenuItem
      active={active}
      onClick={(e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault()
        onClick(e)
      }}
      disabled={disabled}
      text={
        <Tooltip2 disabled={!disabled} content="Already selected">
          <Flex align="center">
            {multi && <Checkbox checked={checked} disabled={disabled} />}
            <HighlightedText text={startCase(text)} query={query} />
          </Flex>
        </Tooltip2>
      }
    />
  )
}
